import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const microeconomicsUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Production, Cost, and the Perfect Competition Model",
  examWeight: "22–25%",
  focus: "Production Choices and Behavior (PRD), Costs, Benefits, and Marginal Analysis (CBA)",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: The Production Function",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔑 Key Definitions</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Production Function" 
                definition="The relationship between the quantity of inputs a firm uses and the quantity of output it produces."
              />
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h4 className="font-bold text-slate-900">Inputs (Factors of Production)</h4>
                  <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                    <li><strong>Fixed Inputs:</strong> Cannot be changed easily in the short run (e.g., factory size, heavy machinery).</li>
                    <li><strong>Variable Inputs:</strong> Can be changed quickly to change output levels (e.g., labor, raw materials).</li>
                  </ul>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h4 className="font-bold text-slate-900">Time Horizons</h4>
                  <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                    <li><strong>Short Run:</strong> Period where at least one input is fixed (usually capital).</li>
                    <li><strong>Long Run:</strong> Period where <strong>all</strong> inputs are variable. Firms can build new factories or leave the industry.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Measuring Production</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Total Product (TP or Q)</h4>
                <p className="text-sm text-slate-700">The total quantity of output produced.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <h4 className="font-bold text-indigo-900">Marginal Product (MP)</h4>
                  <p className="text-xs text-slate-600 mb-2">Additional output from adding one more unit of variable input (usually labor).</p>
                  <BlockMath math="MP = \frac{\Delta \text{Total Product}}{\Delta \text{Inputs}}" />
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <h4 className="font-bold text-indigo-900">Average Product (AP)</h4>
                  <p className="text-xs text-slate-600 mb-2">The output per unit of input.</p>
                  <BlockMath math="AP = \frac{\text{Total Product}}{\text{Units of Input}}" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ The Law of Diminishing Marginal Returns</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              As you add more variable inputs (workers) to a set of fixed inputs (machinery), the additional output generated by each new worker will eventually decrease.
            </p>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-4">
              <h4 className="font-bold text-orange-900">Why?</h4>
              <p className="text-sm text-slate-700">"Too many cooks in the kitchen." Fixed resources become overcrowded or overutilized.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <h4 className="bg-slate-100 px-4 py-2 font-bold text-slate-800 border-b">Three Stages of Returns</h4>
              <div className="p-4 space-y-2">
                <p className="text-sm text-slate-700"><strong>1. Increasing Marginal Returns:</strong> MP is rising (specialization occurs).</p>
                <p className="text-sm text-slate-700"><strong>2. Diminishing Marginal Returns:</strong> MP is falling but still positive (TP grows at a slower rate).</p>
                <p className="text-sm text-slate-700"><strong>3. Negative Marginal Returns:</strong> MP is negative (TP decreases; workers get in each other's way).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 Graphical Relationships</h3>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">MP and TP</h4>
                <ul className="list-disc pl-5 text-sm text-slate-700">
                  <li>When MP is positive, TP is increasing.</li>
                  <li>When MP is zero, TP is maximized (peak).</li>
                  <li>When MP is negative, TP decreases.</li>
                </ul>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">MP and AP</h4>
                <ul className="list-disc pl-5 text-sm text-slate-700">
                  <li>If <InlineMath math="MP > AP" />, AP rises (pulls average up).</li>
                  <li>If <InlineMath math="MP < AP" />, AP falls (pulls average down).</li>
                  <li>MP crosses AP at the <strong>maximum</strong> of the AP curve.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Short-Run Production Costs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Types of Costs</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Fixed (FC)</h4>
                <p className="text-xs text-slate-700 mt-1">Do not change with output (e.g., rent, insurance). Paid even if output is zero.</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Variable (VC)</h4>
                <p className="text-xs text-slate-700 mt-1">Change as output changes (e.g., wages, raw materials).</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Total (TC)</h4>
                <p className="text-xs text-slate-700 mt-1">Sum of all costs.</p>
                <div className="mt-2 text-center font-bold text-blue-800">
                   <InlineMath math="TC = FC + VC" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 Per-Unit Costs (Average Curves)</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800 mb-2">Average Costs</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center bg-white p-2 rounded border border-slate-100">
                    <span className="text-sm font-semibold">AFC:</span>
                    <InlineMath math="\frac{TFC}{Q}" />
                  </div>
                  <div className="flex justify-between items-center bg-white p-2 rounded border border-slate-100">
                    <span className="text-sm font-semibold">AVC:</span>
                    <InlineMath math="\frac{TVC}{Q}" />
                  </div>
                  <div className="flex justify-between items-center bg-white p-2 rounded border border-slate-100">
                    <span className="text-sm font-semibold">ATC:</span>
                    <InlineMath math="\frac{TC}{Q} \text{ or } AFC + AVC" />
                  </div>
                </div>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900 mb-2">Marginal Cost (MC)</h4>
                <p className="text-xs text-slate-600 mb-4">The cost of producing one additional unit.</p>
                <BlockMath math="MC = \frac{\Delta TC}{\Delta Q}" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎨 Graphical Relationships (Cost Curves)</h3>
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">The MC Curve</h4>
                <p className="text-sm text-slate-700">Shaped like a <strong>"Nike Swoosh."</strong> It falls initially due to increasing returns, then rises due to diminishing marginal returns.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">The U-Shapes and Intersections</h4>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-2">
                  <li><strong>The Crucial Rule:</strong> The MC curve crosses ATC and AVC curves at their <strong>minimum points</strong>.</li>
                  <li>If <InlineMath math="MC < ATC" />, ATC falls. If <InlineMath math="MC > ATC" />, ATC rises.</li>
                  <li><strong>Vertical Distance:</strong> The distance between ATC and AVC represents <strong>AFC</strong>. These curves get closer together as Q increases because AFC approaches zero.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Long-Run Production Costs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏢 LRATC & Economies of Scale</h3>
            <KeyTerm 
              term="LRATC (Envelope Curve)" 
              definition="Made up of the lowest points of all possible short-run ATC curves; represents lowest cost per unit when all inputs are variable."
            />
            <div className="mt-6 space-y-4">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">1. Economies of Scale</h4>
                <p className="text-sm text-slate-700">LRATC <strong>decreases</strong> as output increases.</p>
                <p className="text-xs text-slate-500 mt-1"><strong>Causes:</strong> Specialization, bulk purchasing, better technology.</p>
              </div>
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">2. Constant Returns to Scale</h4>
                <p className="text-sm text-slate-700">LRATC stays the same as output increases.</p>
              </div>
              <div className="bg-gradient-to-r from-rose-50 to-red-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-rose-900">3. Diseconomies of Scale</h4>
                <p className="text-sm text-slate-700">LRATC <strong>increases</strong> as output increases.</p>
                <p className="text-xs text-slate-500 mt-1"><strong>Causes:</strong> Bureaucracy, communication problems, management inefficiencies ("too big to manage").</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Types of Profit",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💸 Costs and Decision Making</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-slate-900 mb-2">Explicit Costs</h4>
                <p className="text-sm text-slate-700 italic">"Out-of-pocket"</p>
                <p className="text-sm text-slate-600 mt-1">Wages, rent, materials. Involves direct monetary transfer.</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-slate-900 mb-2">Implicit Costs</h4>
                <p className="text-sm text-slate-700 italic">"Opportunity Costs"</p>
                <p className="text-sm text-slate-600 mt-1">Forgone income, owner's salary, interest lost on invested capital.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Profit Definitions</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Accounting Profit</h4>
                <BlockMath math="\text{TR} - \text{Explicit Costs}" />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Economic Profit</h4>
                <BlockMath math="\text{TR} - (\text{Explicit} + \text{Implicit Costs})" />
                <p className="text-xs text-purple-800 mt-2 font-semibold">Note: Economic Profit is always less than Accounting Profit.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Normal Profit</h4>
                <p className="text-sm text-slate-700"><strong>Zero Economic Profit.</strong> Firm covers all costs. Owner is doing exactly as well as their next best alternative. Breakeven point to keep a firm in the industry.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Profit Maximization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Marginal Analysis</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <KeyTerm term="Marginal Revenue (MR)" definition="Additional revenue from selling one more unit." />
              <KeyTerm term="Marginal Cost (MC)" definition="Additional cost of producing one more unit." />
            </div>
            
            <div className="bg-slate-900 text-white p-8 rounded-2xl shadow-xl text-center">
              <h4 className="text-yellow-400 font-bold text-lg mb-2 underline tracking-widest uppercase">The Profit-Maximizing Rule</h4>
              <p className="text-sm mb-4 text-slate-300">Produce at the quantity where:</p>
              <div className="text-4xl font-bold">
                <BlockMath math="MR = MC" />
              </div>
            </div>

            <div className="mt-8 space-y-4">
              <h4 className="font-bold text-slate-800">The Logic:</h4>
              <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-2xl">➕</div>
                <p className="text-sm text-slate-700"><strong>MR &gt; MC:</strong> Producing the next unit brings in more money than it costs. <strong>Produce more.</strong></p>
              </div>
              <div className="flex items-center gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="text-2xl">➖</div>
                <p className="text-sm text-slate-700"><strong>MC &gt; MR:</strong> Producing the next unit costs more than it brings in. <strong>Produce less.</strong></p>
              </div>
              <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl">🎯</div>
                <p className="text-sm text-slate-700"><strong>MR = MC:</strong> You have squeezed every dollar of profit out of the market. <strong>Stop here.</strong></p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-6",
      title: "Topic 3.6: Short-Run and Long-Run Decisions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛑 Short-Run: The Shut-Down Rule</h3>
            <p className="text-slate-700 mb-4">Assumes Fixed Costs must be paid regardless.</p>
            <div className="space-y-3">
              <div className="p-4 bg-red-100 rounded-lg border border-red-300">
                <h4 className="font-bold text-red-900">Rule: Shut down if P &lt; AVC</h4>
                <p className="text-xs text-red-800 mt-1">Logic: Firm loses more money operating (FC + part of VC) than by shutting down (just FC).</p>
              </div>
              <div className="p-4 bg-blue-100 rounded-lg border border-blue-300">
                <h4 className="font-bold text-blue-900">Operate at Loss: If P &gt; AVC but P &lt; ATC</h4>
                <p className="text-xs text-blue-800 mt-1">Logic: Revenue covers all variable costs and contributes to fixed costs.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚪 Long-Run: Entry and Exit</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-green-700">If Economic Profit &gt; 0</h4>
                <p className="text-sm text-slate-700">Firms <strong>enter</strong> $\to$ Market supply increases $\to$ Price decreases $\to$ Profit eliminated.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-red-700">If Economic Profit &lt; 0</h4>
                <p className="text-sm text-slate-700">Firms <strong>exit</strong> $\to$ Market supply decreases $\to$ Price increases $\to$ Losses eliminated.</p>
              </div>
              <ImportantNote>
                <strong>Long-Run Equilibrium:</strong> Entry/Exit continues until <strong>Economic Profit = 0</strong> (Normal Profit).
              </ImportantNote>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-7",
      title: "Topic 3.7: Perfect Competition",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌾 Characteristics</h3>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li><strong>Many small buyers and sellers.</strong></li>
              <li><strong>Identical (Homogeneous) Products</strong> (e.g., corn, strawberries).</li>
              <li><strong>No Barriers to Entry/Exit:</strong> Easy to start or stop.</li>
              <li><strong>Perfect Information:</strong> Everyone knows prices.</li>
              <li><strong>Price Takers:</strong> No control over price; firm takes the market price.</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 Side-by-Side Graphs</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded border border-slate-200">
                <h4 className="font-bold text-center border-b mb-2">The Market</h4>
                <p className="text-xs text-slate-600">Standard Demand (downward) and Supply (upward). Equilibrium establishes market price (<InlineMath math="P_e" />).</p>
              </div>
              <div className="bg-white p-4 rounded border border-slate-200">
                <h4 className="font-bold text-center border-b mb-2">The Firm</h4>
                <p className="text-xs text-slate-600">Demand is <strong>Perfectly Elastic</strong>. The firm produces where <InlineMath math="MR = MC" />.</p>
              </div>
            </div>
            <Mnemonic 
              title="MR. DARP" 
              content="Marginal Revenue = Demand = Average Revenue = Price" 
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏁 Short-Run vs Long-Run Positions</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="p-3 bg-green-50 rounded border border-green-200 text-xs"><strong>Profit:</strong> Price is ABOVE ATC at MR=MC.</div>
              <div className="p-3 bg-blue-50 rounded border border-blue-200 text-xs"><strong>Normal Profit:</strong> Price TOUCHES minimum of ATC.</div>
              <div className="p-3 bg-orange-50 rounded border border-orange-200 text-xs"><strong>Loss:</strong> Price is below ATC but above AVC.</div>
              <div className="p-3 bg-red-50 rounded border border-red-200 text-xs"><strong>Shut Down:</strong> Price is below AVC.</div>
            </div>
            <div className="mt-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-900 mb-1 text-sm">Long-Run Adjustment:</h4>
              <p className="text-xs text-slate-700">Profit in Short Run $\to$ Entry $\to$ Supply shifts Right $\to$ Price falls $\to$ Firm's MR/D curve shifts Down $\to$ Profit disappears.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✨ Efficiency</h3>
            <p className="text-sm text-slate-700 mb-4">Perfect competition achieves both in the long run:</p>
            <div className="space-y-3">
              <div className="p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg">
                <h4 className="font-bold text-indigo-900">Productive Efficiency</h4>
                <p className="text-xs text-slate-700">Producing at lowest cost: <strong>Price = Min ATC</strong></p>
              </div>
              <div className="p-4 bg-teal-50 border-l-4 border-teal-500 rounded-r-lg">
                <h4 className="font-bold text-teal-900">Allocative Efficiency</h4>
                <p className="text-xs text-slate-700">Producing what society wants: <strong>Price = MC</strong> (or MB = MC)</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default microeconomicsUnit3Notes;